import { emitHintSignal } from '@flobi/asset-library/hints';
import { Menu, useMenu, type MenuEntry } from '../../ui/menu';
import { tip } from '../../ui/tooltip';
import { Icon, ICON_SIZE } from '../chrome/icons';
import type { RailButton } from '../tools/types';

const descriptions: Record<string, { label: string; note: string }> = {
  object: { label: 'Object selection', note: 'Select and move whole objects' },
  point: { label: 'Point selection', note: 'Select individual points on a path' },
  area: { label: 'Marquee', note: 'Drag a box to select points' },
  lasso: { label: 'Lasso', note: 'Draw a loop to select points' },
};

/** The same mode switch stays reachable on desktop and in every phone tray state. */
export function SelectionModePicker({ modes, active, phone = false }: { modes: readonly RailButton[]; active: boolean; phone?: boolean }) {
  const menu = useMenu();
  const chosen = active ? modes.find(mode => mode.pressed) : undefined;
  const label = chosen ? descriptions[chosen.id]?.label ?? 'Selection mode' : 'Selection mode';
  const items: MenuEntry[] = modes.flatMap(mode => [
    ...(mode.id === 'point' ? [{ kind: 'separator' as const }] : []),
    { id: mode.id, label: descriptions[mode.id]?.label ?? mode.title.split(' — ')[0]!,
      note: descriptions[mode.id]?.note, icon: mode.id === 'object' ? 'tools-select' : mode.icon, checked: active && !!mode.pressed,
      disabled: mode.disabled,
      onSelect: () => { emitHintSignal('tool.select', { viaButton: true }); mode.run({ more: false }); } },
  ]);
  return <>
    <button type="button" ref={menu.ref} className={phone ? 'phone-tray-slot selection-mode-picker' : 'dock-action dock-select selection-mode-picker'}
      aria-label="Selection mode" aria-haspopup="menu" aria-expanded={menu.open} aria-pressed={active}
      {...tip(label)} data-guide="select" onClick={menu.toggle}>
      <span className={phone ? 'phone-tray-mark' : 'selection-mode-mark'}><Icon name="tools-select" size={phone ? ICON_SIZE.navigation : ICON_SIZE.control} /><Icon name="chevron" size={ICON_SIZE.indicator} /></span>
      {phone && <span className="phone-tray-label">Select</span>}
    </button>
    <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} items={items} current={chosen?.id}
      placement={phone ? 'above' : 'below'} label="Selection mode" width={264} />
  </>;
}
